<template>
  <div id="app" class="container-fluid">
    <!-- Header with Patient Info -->

    <full_head />

    <!-- Main Content -->
    <div class="main-content">
      <div class="row">
        <!-- Left Column - Personal & Medical History -->
        <div class="col-md-6">
          <!-- Personal Information Card -->
          <div class="card">
            <div class="card-header">
              <h3><i class="fas fa-user-circle"></i> Personal Information</h3>
            </div>
            <div class="card-body">
              <div class="info-grid">
                <div class="info-item">
                  <label>Full Name</label>
                  <p>{{ patient.fullName }}</p>
                </div>
                <div class="info-item">
                  <label>Age</label>
                  <p>{{ patient.age }} years</p>
                </div>
                <div class="info-item">
                  <label>Date of Birth</label>
                  <p>{{ patient.dateOfBirth }}</p>
                </div>
                <div class="info-item">
                  <label>Marital Status</label>
                  <p>{{ patient.maritalStatus }}</p>
                </div>
                <div class="info-item">
                  <label>Education</label>
                  <p>{{ patient.education }}</p>
                </div>
                <div class="info-item">
                  <label>Occupation</label>
                  <p>{{ patient.occupation }}</p>
                </div>
                <div class="info-item">
                  <label>Husband's Name</label>
                  <p>{{ patient.husbandName }}</p>
                </div>
                <div class="info-item">
                  <label>Husband's Occupation</label>
                  <p>{{ patient.husbandOccupation }}</p>
                </div>
                <div class="info-item full-width">
                  <label>Address</label>
                  <p>{{ patient.address }}</p>
                </div>
                <div class="info-item">
                  <label>Phone Number</label>
                  <p>{{ patient.phone }}</p>
                </div>
                <div class="info-item">
                  <label>Emergency Contact</label>
                  <p>{{ patient.emergencyContact }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Medical History Card -->
          <div class="card">
            <div class="card-header">
              <h3><i class="fas fa-clipboard-list"></i> Medical History</h3>
            </div>
            <div class="card-body">
              <div class="medical-history-section">
                <h4><i class="fas fa-allergies"></i> Medical Conditions</h4>
                <div class="condition-list">
                  <div
                    v-for="(condition, index) in medicalHistory.conditions"
                    :key="index"
                    class="condition-item"
                  >
                    <span class="condition-name">{{ condition.name }}</span>
                    <span class="condition-status" :class="condition.status">
                      {{ condition.status }}
                    </span>
                    <span class="condition-year" v-if="condition.year"
                      >Since {{ condition.year }}</span
                    >
                  </div>
                </div>

                <h4><i class="fas fa-pills"></i> Current Medications</h4>
                <div v-if="medicalHistory.medications.length > 0">
                  <div
                    v-for="(med, index) in medicalHistory.medications"
                    :key="index"
                    class="medication-item"
                  >
                    <span class="med-name">{{ med.name }}</span>
                    <span class="med-dosage">{{ med.dosage }}</span>
                    <span class="med-purpose">{{ med.purpose }}</span>
                  </div>
                </div>
                <p v-else class="no-data">No current medications</p>

                <h4><i class="fas fa-procedures"></i> Allergies</h4>
                <div class="allergies">
                  <!-- FIXED: Separated v-if and v-for -->
                  <template v-if="medicalHistory.allergies.length > 0">
                    <span
                      v-for="(allergy, index) in medicalHistory.allergies"
                      :key="index"
                      class="allergy-tag"
                    >
                      {{ allergy }}
                    </span>
                  </template>
                  <span v-else class="no-data">No known allergies</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Surgical & Obstetric History -->
        <div class="col-md-6">
          <!-- Surgical History Card -->
          <div class="card">
            <div class="card-header">
              <h3><i class="fas fa-scalpel"></i> Surgical History</h3>
            </div>
            <div class="card-body">
              <div class="surgical-history-section">
                <div v-if="surgicalHistory.length > 0">
                  <div
                    v-for="(surgery, index) in surgicalHistory"
                    :key="index"
                    class="surgery-item"
                  >
                    <div class="surgery-header">
                      <span class="surgery-name">{{ surgery.procedure }}</span>
                      <span class="surgery-year">{{ surgery.year }}</span>
                    </div>
                    <div class="surgery-details">
                      <span class="surgery-type">{{ surgery.type }}</span>
                      <span class="surgery-hospital">{{
                        surgery.hospital
                      }}</span>
                    </div>
                    <div
                      class="surgery-complications"
                      v-if="surgery.complications"
                    >
                      <small>Complications: {{ surgery.complications }}</small>
                    </div>
                  </div>
                </div>
                <div v-else class="no-data">
                  <i class="fas fa-ban"></i> No surgical history recorded
                </div>
              </div>
            </div>
          </div>

          <!-- Obstetric History Card -->
          <div class="card">
            <div class="card-header">
              <h3><i class="fas fa-baby-carriage"></i> Obstetric History</h3>
            </div>
            <div class="card-body">
              <div class="obstetric-summary">
                <div class="gp-score">
                  <div class="gp-item">
                    <span class="gp-label">Gravida</span>
                    <span class="gp-value">{{ obstetricHistory.gravida }}</span>
                  </div>
                  <div class="gp-item">
                    <span class="gp-label">Para</span>
                    <span class="gp-value">{{ obstetricHistory.para }}</span>
                  </div>
                  <div class="gp-item">
                    <span class="gp-label">Abortions</span>
                    <span class="gp-value">{{
                      obstetricHistory.abortions
                    }}</span>
                  </div>
                  <div class="gp-item">
                    <span class="gp-label">Living Children</span>
                    <span class="gp-value">{{
                      obstetricHistory.livingChildren
                    }}</span>
                  </div>
                </div>

                <h4>Previous Pregnancy Details</h4>
                <div v-if="obstetricHistory.previousPregnancies.length > 0">
                  <table class="table">
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Outcome</th>
                        <th>Gestation</th>
                        <th>Birth Weight</th>
                        <th>Delivery Mode</th>
                        <th>Complications</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(
                          pregnancy, index
                        ) in obstetricHistory.previousPregnancies"
                        :key="index"
                      >
                        <td>{{ pregnancy.year }}</td>
                        <td>
                          <span
                            class="outcome-badge"
                            :class="getOutcomeClass(pregnancy.outcome)"
                          >
                            {{ pregnancy.outcome }}
                          </span>
                        </td>
                        <td>{{ pregnancy.gestation }} weeks</td>
                        <td>{{ pregnancy.birthWeight }} kg</td>
                        <td>{{ pregnancy.deliveryMode }}</td>
                        <td>
                          <span
                            v-if="pregnancy.complications"
                            class="complication-indicator"
                          >
                            <i class="fas fa-exclamation-triangle"></i> Yes
                          </span>
                          <span v-else>None</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="no-data">No previous pregnancy history</div>
              </div>
            </div>
          </div>

          <!-- Family History Card -->
          <div class="card">
            <div class="card-header">
              <h3><i class="fas fa-family"></i> Family History</h3>
            </div>
            <div class="card-body">
              <div class="family-history">
                <div
                  class="family-member"
                  v-for="(history, member) in familyHistory"
                  :key="member"
                >
                  <h5>{{ member }}</h5>
                  <div class="family-conditions">
                    <template v-if="history.length > 0">
                      <span
                        v-for="(condition, index) in history"
                        :key="index"
                        class="family-condition-tag"
                      >
                        {{ condition }}
                      </span>
                    </template>
                    <span v-else class="no-data">No significant history</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Stats Bar -->
      <div class="stats-bar">
        <div class="stat-item">
          <div class="stat-value">{{ obstetricHistory.gravida }}</div>
          <div class="stat-label">Total Pregnancies</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ obstetricHistory.livingChildren }}</div>
          <div class="stat-label">Living Children</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ surgicalHistory.length }}</div>
          <div class="stat-label">Surgeries</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ patient.ancVisits }}</div>
          <div class="stat-label">ANC Visits</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">
            {{ tetanusInjections.completed }}/{{ tetanusInjections.total }}
          </div>
          <div class="stat-label">Tetanus Doses</div>
        </div>
      </div>
    </div>

    <!-- Edit Profile Modal (Placeholder Structure) -->
    <div class="modal-overlay" v-if="showEditModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Edit Maternal Profile</h3>
          <button class="modal-close" @click="showEditModal = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <!-- Modal content will be implemented separately -->
          <p>Edit form will be implemented here with Bootstrap modal</p>
          <div class="modal-actions">
            <button class="btn btn-secondary" @click="showEditModal = false">
              Cancel
            </button>
            <button class="btn btn-primary" @click="saveProfile">
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import anc_head from "./mch/anc/anc_head.vue";
import full_head from "./mch/anc/full_head.vue";
export default {
  name: "MaternalProfile",
  data() {
    return {
      showEditModal: false,
      patient: {
        id: "ANC-2023-07891",
        fullName: "Sarah Johnson",
        age: 32,
        dateOfBirth: "1991-05-15",
        maritalStatus: "Married",
        education: "Bachelor's Degree",
        occupation: "Teacher",
        husbandName: "Michael Johnson",
        husbandOccupation: "Engineer",
        address: "123 Main Street, Springfield, IL 62704",
        phone: "(555) 123-4567",
        emergencyContact: "(555) 987-6543 (Husband)",
        gestationalAge: 28,
        lastVisit: "2023-11-15",
        ancVisits: 5,
      },
      medicalHistory: {
        conditions: [
          { name: "Hypertension", status: "Controlled", year: 2020 },
          { name: "Asthma", status: "Intermittent", year: 2015 },
          { name: "Anemia", status: "Resolved", year: 2022 },
        ],
        medications: [
          {
            name: "Folic Acid",
            dosage: "400mg daily",
            purpose: "Pregnancy supplement",
          },
          {
            name: "Iron Supplement",
            dosage: "65mg daily",
            purpose: "Anemia prevention",
          },
        ],
        allergies: ["Penicillin", "Sulfa drugs"],
      },
      surgicalHistory: [
        {
          procedure: "Appendectomy",
          year: 2015,
          type: "Emergency",
          hospital: "Springfield General",
          complications: "None",
        },
        {
          procedure: "Cyst Removal",
          year: 2018,
          type: "Elective",
          hospital: "City Medical Center",
          complications: "Minor infection",
        },
        {
          procedure: "Cesarean Section",
          year: 2020,
          type: "Obstetric",
          hospital: "Women's Hospital",
          complications: "Post-operative fever",
        },
      ],
      obstetricHistory: {
        gravida: 3,
        para: 2,
        abortions: 0,
        livingChildren: 2,
        previousPregnancies: [
          {
            year: 2020,
            outcome: "Live Birth",
            gestation: 39,
            birthWeight: "3.2",
            deliveryMode: "Cesarean Section",
            complications: true,
          },
          {
            year: 2022,
            outcome: "Live Birth",
            gestation: 40,
            birthWeight: "3.5",
            deliveryMode: "Vaginal",
            complications: false,
          },
        ],
      },
      familyHistory: {
        Mother: ["Type 2 Diabetes", "Hypertension"],
        Father: ["Heart Disease"],
        Siblings: ["Asthma"],
        "Maternal Grandmother": ["Breast Cancer"],
        "Paternal Grandmother": ["Hypertension"],
      },
      tetanusInjections: {
        completed: 2,
        total: 5,
        doses: [
          { date: "2023-09-01", dose: "TT1", nextDue: "2023-10-01" },
          { date: "2023-10-05", dose: "TT2", nextDue: "2023-11-05" },
        ],
      },
    };
  },
  components: {
    // anc_head,
    full_head,
  },
  methods: {
    editProfile() {
      this.showEditModal = true;
    },
    saveProfile() {
      // Save logic will be implemented in modal
      this.showEditModal = false;
      alert("Profile saved successfully!");
    },
    navigateTo(section) {
      // Navigation logic for different sections
      switch (section) {
        case "previousPregnancies":
          alert("Navigating to Previous Pregnancies section");
          break;
        case "antenatalProfile":
          alert("Navigating to Antenatal Profile section");
          break;
        case "presentPregnancy":
          alert("Navigating to Present Pregnancy table");
          break;
        case "weightChart":
          alert("Opening Weight Monitoring Chart");
          break;
        case "tetanus":
          alert("Showing Tetanus Injection records");
          break;
        case "treatment":
          alert("Opening Treatment Record");
          break;
      }
      // In a real app, you would use Vue Router to navigate or show/hide components
    },
    getOutcomeClass(outcome) {
      // Helper method for outcome badge classes
      const outcomeMap = {
        "Live Birth": "live",
        Stillbirth: "stillbirth",
        Miscarriage: "miscarriage",
        Abortion: "miscarriage",
      };
      return outcomeMap[outcome] || "";
    },
  },
};
</script>

<style scoped>
/* All your existing CSS styles remain exactly the same */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.container-fluid {
  padding: 20px;
  background-color: #f8f9fa;
}

.header {
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 25px;
  overflow: hidden;
}

.patient-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.patient-main-info h1 {
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 10px;
}

.patient-main-info h1 i {
  margin-right: 10px;
}

.patient-id-section {
  display: flex;
  gap: 30px;
  font-size: 16px;
}

.patient-id-section span {
  background: rgba(255, 255, 255, 0.2);
  padding: 5px 15px;
  border-radius: 20px;
  font-weight: 600;
}

.action-buttons .btn {
  font-size: 16px;
  padding: 12px 24px;
  border-radius: 8px;
}

.navigation-tabs {
  display: flex;
  background: white;
  padding: 15px 30px;
  gap: 10px;
  overflow-x: auto;
}

.nav-btn {
  flex: 1;
  min-width: 180px;
  padding: 15px 20px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  background: white;
  color: #495057;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.nav-btn:hover {
  border-color: #667eea;
  background: #f8f9fe;
  transform: translateY(-2px);
}

.nav-btn i {
  font-size: 18px;
}

.main-content {
  margin-top: 20px;
}

.card {
  background: white;
  border-radius: 10px;
  margin-bottom: 25px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  border: none;
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px;
  border-bottom: 2px solid #dee2e6;
}

.card-header h3 {
  margin: 0;
  color: #495057;
  font-size: 20px;
  font-weight: 600;
}

.card-header h3 i {
  margin-right: 10px;
  color: #667eea;
}

.card-body {
  padding: 25px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.info-item {
  margin-bottom: 20px;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-item label {
  display: block;
  color: #6c757d;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-item p {
  margin: 0;
  font-size: 16px;
  color: #212529;
  font-weight: 500;
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f4;
}

.medical-history-section h4,
.surgical-history-section h4 {
  color: #495057;
  margin: 25px 0 15px;
  font-size: 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e9ecef;
}

.medical-history-section h4 i {
  margin-right: 10px;
  color: #28a745;
}

.condition-list,
.medication-list,
.surgery-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.condition-item,
.medication-item,
.surgery-item {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid #667eea;
}

.condition-header,
.surgery-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.condition-name,
.med-name,
.surgery-name {
  font-weight: 600;
  color: #212529;
}

.condition-status {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 15px;
  font-weight: 600;
}

.condition-status.controlled {
  background: #d4edda;
  color: #155724;
}

.condition-status.resolved {
  background: #cce5ff;
  color: #004085;
}

.condition-status.intermittent {
  background: #fff3cd;
  color: #856404;
}

.condition-year,
.surgery-year {
  font-size: 14px;
  color: #6c757d;
}

.medication-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.med-dosage {
  color: #dc3545;
  font-weight: 600;
}

.med-purpose {
  color: #6c757d;
  font-size: 14px;
}

.surgery-details {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #6c757d;
  margin-bottom: 8px;
}

.surgery-complications {
  padding: 8px;
  background: #fff3cd;
  border-radius: 5px;
  font-size: 13px;
  color: #856404;
}

.allergies {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.allergy-tag {
  background: #f8d7da;
  color: #721c24;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
}

.obstetric-summary {
  padding: 15px;
}

.gp-score {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  margin-bottom: 30px;
}

.gp-item {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 10px;
}

.gp-label {
  display: block;
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 5px;
}

.gp-value {
  display: block;
  font-size: 32px;
  font-weight: 700;
}

.table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.table th {
  background: #f8f9fa;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
}

.table td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
}

.table tr:hover {
  background: #f8f9fa;
}

.outcome-badge {
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 600;
}

.outcome-badge.live {
  background: #d4edda;
  color: #155724;
}

.outcome-badge.stillbirth {
  background: #f8d7da;
  color: #721c24;
}

.outcome-badge.miscarriage {
  background: #fff3cd;
  color: #856404;
}

.complication-indicator {
  color: #dc3545;
  font-weight: 600;
}

.complication-indicator i {
  margin-right: 5px;
}

.family-history {
  padding: 15px;
}

.family-member {
  margin-bottom: 20px;
}

.family-member h5 {
  color: #495057;
  margin-bottom: 10px;
  font-size: 16px;
  font-weight: 600;
}

.family-conditions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.family-condition-tag {
  background: #e9ecef;
  color: #495057;
  padding: 5px 15px;
  border-radius: 15px;
  font-size: 14px;
}

.stats-bar {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
  margin-top: 30px;
}

.stat-item {
  background: white;
  padding: 25px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s;
}

.stat-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}

.stat-value {
  font-size: 36px;
  font-weight: 700;
  color: #667eea;
  margin-bottom: 8px;
}

.stat-label {
  color: #6c757d;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
}

.no-data {
  text-align: center;
  padding: 30px;
  color: #6c757d;
  font-style: italic;
  background: #f8f9fa;
  border-radius: 8px;
}

.no-data i {
  font-size: 40px;
  margin-bottom: 15px;
  display: block;
  color: #adb5bd;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 10px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 24px;
}

.modal-close {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
}

.modal-body {
  padding: 30px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

@media (max-width: 768px) {
  .info-grid {
    grid-template-columns: 1fr;
  }

  .gp-score {
    grid-template-columns: repeat(2, 1fr);
  }

  .stats-bar {
    grid-template-columns: repeat(2, 1fr);
  }

  .navigation-tabs {
    flex-wrap: wrap;
  }

  .nav-btn {
    min-width: 150px;
  }
}

@media (max-width: 576px) {
  .patient-banner {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }

  .patient-id-section {
    flex-direction: column;
    gap: 10px;
  }

  .gp-score {
    grid-template-columns: 1fr;
  }

  .stats-bar {
    grid-template-columns: 1fr;
  }
}
</style>
